import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Send, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const bc = { borderColor: "var(--ll-border)" };

export default function ShareVideoButton({ shared }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-sm px-3 py-2 border bg-white hover:bg-[var(--ll-mint)] inline-flex items-center gap-1.5 transition-colors" style={bc} data-testid="share-outlook-video-btn">
        <Mail size={14} /> Email outlook video to client
      </button>
      {open && <ShareVideoDialog shared={shared} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareVideoDialog({ shared, onClose }) {
  const [clients, setClients] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/users").then((r) => setClients(r.data.filter((u) => u.role === "client" && u.is_active !== false))).catch(() => setClients([]));
  }, []);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/training/share-client-video", { client_ids: [...picked], note });
      if (r.data.sent.length) toast.success(`Outlook video emailed to ${r.data.sent.length} client${r.data.sent.length === 1 ? "" : "s"}`);
      if (r.data.failed.length) setError(`Couldn't deliver to ${r.data.failed.join(", ")}. Check the address and try again.`);
      if (r.data.sent.length) onClose();
    } catch (err) { setError(err?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="share-video-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Send size={16} /> Email the 60-second outlook video</DialogTitle>
          <DialogDescription className="text-[var(--ll-ink-soft)]">Clients get a branded email. The link opens their portal (after sign-in) and the video plays automatically.</DialogDescription>
        </DialogHeader>
        {!shared && (
          <div className="text-xs p-3 border-l-4" style={{ background: "#FFFBEB", borderColor: "#D97706" }} data-testid="share-video-not-shared">
            Turn on <b>Share advisory with client</b> first. Clients can only watch the video once the outlook is shared.
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">Send to</div>
          {!clients ? <Loader2 size={16} className="animate-spin" /> : clients.length === 0 ? (
            <div className="text-sm text-[var(--ll-ink-soft)]" data-testid="share-video-no-clients">No client users in this workspace yet. Invite one from Team.</div>
          ) : (
            <ul className="border max-h-52 overflow-y-auto divide-y" style={bc} data-testid="share-video-clients">
              {clients.map((c) => (
                <li key={c.id}>
                  <label className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[var(--ll-mint)]">
                    <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} data-testid={`share-video-client-${c.id}`} />
                    <span className="font-medium">{c.name}</span><span className="text-[var(--ll-ink-soft)] text-xs">{c.email}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">Personal note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={3} placeholder="e.g. Have a look before our call on Friday."
            className="mt-1 w-full px-3 py-2 border text-sm" style={bc} data-testid="share-video-note" />
        </label>
        {error && <div className="text-xs p-3 border-l-4 text-[var(--ll-error)]" style={{ background: "#FEF2F2", borderColor: "var(--ll-error)" }} data-testid="share-video-error">{error}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border text-sm inline-flex items-center justify-center gap-1.5" style={bc} data-testid="share-video-cancel"><X size={14} /> Cancel</button>
          <button onClick={send} disabled={busy || !picked.size || !shared} className="flex-1 px-4 py-2 text-sm text-white bg-[var(--ll-ink)] inline-flex items-center justify-center gap-1.5 disabled:opacity-40" data-testid="share-video-send">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send to {picked.size || ""} client{picked.size === 1 ? "" : "s"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
